import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { cotAxios, COTACAO_API } from "./api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ShoppingCart, FileDown, Search, ArrowLeft } from "lucide-react";

const brl = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return iso; } };

export default function CotacaoPedidosPage() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState("");

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await cotAxios.get("/cotacao/pedidos");
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar");
    } finally { setLoading(false); }
  };

  const baixarPdf = async (p) => {
    try {
      const token = localStorage.getItem("cotacao_token");
      const res = await fetch(`${COTACAO_API}/cotacao/pedidos/${p.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pedido-${String(p.numero).padStart(4, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch { toast.error("Erro ao baixar PDF"); }
  };

  const filtered = rows.filter((p) => {
    const q = busca.toLowerCase().trim();
    if (!q) return true;
    return [String(p.numero), p.fornecedor?.razao_social, p.fornecedor?.nome_fantasia].some((v) => (v || "").toString().toLowerCase().includes(q));
  });

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="cot-pedidos-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ShoppingCart className="w-6 h-6 text-blue-700" /> Pedidos de Compra</h1>
          <p className="text-sm text-slate-500">Total: {rows.length}</p>
        </div>
      </div>

      <div className="mb-4 relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <Input placeholder="Buscar por número ou fornecedor..." value={busca} onChange={(e) => setBusca(e.target.value)} className="pl-9" data-testid="busca-pedido" />
      </div>

      {loading ? (
        <div className="text-center py-10 text-slate-500">Carregando...</div>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">
          Nenhum pedido gerado ainda. Aprove propostas no mapa de uma cotação e clique em &quot;Gerar Pedido&quot;.
        </CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map((p) => (
            <Card key={p.id} data-testid={`pedido-${p.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold">Pedido nº {String(p.numero).padStart(4, "0")}</div>
                      <span className="text-xs text-slate-500">(nº fornecedor {String(p.numero_fornecedor).padStart(4, "0")})</span>
                    </div>
                    <div className="text-sm text-slate-700 mt-1">
                      <span className="font-medium">{p.fornecedor?.razao_social}</span>
                      {p.fornecedor?.cnpj && <span className="text-slate-500 text-xs ml-2">CNPJ {p.fornecedor.cnpj}</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 flex gap-4 flex-wrap">
                      <span>Cotação nº {p.cotacao_numero}</span>
                      <span>{(p.itens || []).length} item(ns)</span>
                      <span>Emitido em {fmtDate(p.created_at)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-500">Total</div>
                    <div className="font-semibold text-lg text-blue-700">{brl(p.total_geral)}</div>
                    {p.economia_total > 0 && <div className="text-xs text-emerald-700">Econ. {brl(p.economia_total)}</div>}
                  </div>
                  <div>
                    <Button size="sm" variant="outline" onClick={() => baixarPdf(p)} data-testid={`btn-pdf-${p.id}`}>
                      <FileDown className="w-4 h-4 mr-1" /> PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
